// Bundle loader + adapter. Closes the gap between the design's rich bundle shape
// and the (plainer) real exported bundles: normalizes precomputed list↔dict,
// synthesizes an as-read reframe profile when absent, and fills reader copy from
// labels.ts. After normalize(), panels can rely on the design-shape fields.

import type {
  Bundle, ReframeResult, ReframeProfile, StructuralDef, NumericAxis, Scores,
} from "./types";
import { NUMERIC_AXES } from "./types";
import { AXIS_LABELS, AXIS_ENDS, leanLabel, outletDisplay, categoricals } from "./labels";

const DEFAULT_STRUCTURAL: Record<string, StructuralDef> = {
  agent_of_force: { label: "Agent of force", options: ["passive_no_agent", "officer_agent"],
    display: { passive_no_agent: "Hidden / passive", officer_agent: "Officer named" } },
  police_claim_stance: { label: "Police claims", options: ["asserted_fact", "attributed", "contested"],
    display: { asserted_fact: "Stated as fact", attributed: "Attributed", contested: "Contested" } },
  headline_perspective: { label: "Headline frame", options: ["police", "neutral", "civilian"],
    display: { police: "Police view", neutral: "Neutral", civilian: "Civilian" } },
};

function numericScores(scores: Scores | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  if (!scores) return out;
  for (const ax of NUMERIC_AXES) {
    const v = scores[ax];
    if (typeof v === "number") out[ax] = v;
  }
  return out;
}

function structuralFromScores(scores: Scores | undefined): Record<string, string> {
  if (!scores) return {};
  return {
    agent_of_force: String(scores.agent_of_force ?? "officer_agent"),
    police_claim_stance: String(scores.police_claim_stance ?? "attributed"),
    headline_perspective: String(scores.headline_perspective ?? "neutral"),
  };
}

/** Normalize any bundle (real or design) into the design-shape the panels expect. */
export function normalize(raw: any): Bundle {
  const b: Bundle = JSON.parse(JSON.stringify(raw));

  // ---- incident date label ----
  if (!b.incident.date_label && b.incident.date) {
    b.incident.date_label = formatDate(b.incident.date);
  }

  // ---- target outlet display ----
  if (!b.target.outlet_display || b.target.outlet_display === b.target.outlet) {
    b.target.outlet_display = outletDisplay(b.target.outlet);
  }

  // ---- lean summary ----
  if (!b.lean_summary) b.lean_summary = synthLeanSummary(b);

  // ---- categoricals ----
  if (!b.categoricals || !b.categoricals.length) {
    b.categoricals = categoricals(b.target.scores);
  }

  // ---- axis_profile reader-copy fill ----
  b.axis_profile = (b.axis_profile || []).map((a) => {
    const ax = a.axis as NumericAxis;
    const [lo, hi] = AXIS_ENDS[ax] || ["", ""];
    return {
      ...a,
      label: a.label || AXIS_LABELS[ax] || ax,
      low: a.low ?? lo,
      high: a.high ?? hi,
      lean_label: a.lean_label ?? leanLabel(ax, a.target_value),
      dim: a.dim ?? (a.target_value === a.midpoint && a.incident_range === 0),
    };
  });

  // ---- reframe block fill ----
  const rf: any = b.reframe || {};
  rf.enabled = rf.enabled ?? true;
  rf.axes = rf.axes && rf.axes.length ? rf.axes : [...NUMERIC_AXES];
  rf.axis_labels = rf.axis_labels && Object.keys(rf.axis_labels).length ? rf.axis_labels : { ...AXIS_LABELS };
  rf.axis_scales = rf.axis_scales && Object.keys(rf.axis_scales).length
    ? rf.axis_scales
    : Object.fromEntries(NUMERIC_AXES.map((ax) => [ax, ax === "systemic_context" ? [0, 3] : [-2, 2]]));
  rf.structural = rf.structural && Object.keys(rf.structural).length ? rf.structural : DEFAULT_STRUCTURAL;
  rf.live = rf.live || { endpoint: "/api/reframe", debounce_ms: 600, session_call_cap: 12, quantize: "round_to_int" };
  if (rf.live.endpoint === "/reframe") rf.live.endpoint = "/api/reframe";

  // precomputed: list -> dict keyed by profile_id
  rf.precomputed = toDict(rf.precomputed);
  rf.profiles = Array.isArray(rf.profiles) ? rf.profiles : [];

  // synthesize an as-read profile + precomputed entry when the bundle has none
  const hasAsRead = rf.profiles.some((p: ReframeProfile) => p.kind === "as_read")
    || Object.values(rf.precomputed).some((r: any) => r.as_read);
  if (!hasAsRead) {
    const pid = "outlet_" + b.target.outlet;
    const asReadProfile: ReframeProfile = {
      profile_id: pid, kind: "as_read",
      display: "As read · " + b.target.outlet_display,
      outlet_domain: b.target.outlet, n_articles: 1,
      axis_means: numericScores(b.target.scores), device_rates: {},
    };
    const reqs = numericScores(b.target.scores);
    const asReadResult: ReframeResult = {
      profile_id: pid, as_read: true, display: asReadProfile.display,
      rewritten_headline: b.target.headline, rewritten_body: b.target.body,
      requested_target: reqs, structural_targets: structuralFromScores(b.target.scores),
      achieved_scores: (b.target.scores as any) || {},
      residuals: Object.fromEntries(Object.keys(reqs).map((k) => [k, 0])),
      change_log: [], diff_ops: [],
      invariant: { ok: true, quotes_preserved: true, facts_added: [], facts_dropped: [] },
      manipulation_check: { intended_direction: "as_read", achieved_direction: "as_read", axes_on_target: rf.axes.length, axes_total: rf.axes.length, landed: true },
    };
    rf.profiles = [asReadProfile, ...rf.profiles];
    rf.precomputed[pid] = asReadResult;
    rf.default_profile_id = pid;
  }
  if (!rf.default_profile_id || !rf.precomputed[rf.default_profile_id]) {
    const asRead = rf.profiles.find((p: ReframeProfile) => p.kind === "as_read");
    rf.default_profile_id = asRead ? asRead.profile_id : rf.profiles[0]?.profile_id;
  }

  // keep only profiles that actually have a precomputed reframe (so every chip works)
  rf.profiles = rf.profiles.filter((p: ReframeProfile) => rf.precomputed[p.profile_id]);
  b.reframe = rf;

  // ---- defensive defaults ----
  b.highlights = b.highlights || [];
  b.sources = b.sources || [];
  b.content_warning = b.content_warning && b.content_warning.length ? b.content_warning : ["police use of force"];
  if (!b.rewrite) b.rewrite = { no_op: true, rewritten_headline: b.target.headline, rewritten_body: b.target.body, optional_context_block: "", change_log: [], diff_ops: [] };
  if (!b.fact_table) b.fact_table = { source_types_present: [], direct_quotes: [], claims: [] };
  return b;
}

function toDict(pc: any): Record<string, ReframeResult> {
  if (!pc) return {};
  if (Array.isArray(pc)) {
    const d: Record<string, ReframeResult> = {};
    for (const r of pc) if (r && r.profile_id) d[r.profile_id] = r;
    return d;
  }
  return pc;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function formatDate(s: string): string {
  // accepts "2019-03-14" or "3/14/2019" / "03/14/2019"
  let y = 0, mo = 0, d = 0;
  if (/^\d{4}-\d{1,2}-\d{1,2}/.test(s)) {
    const [yy, mm, dd] = s.split("-").map((x) => parseInt(x, 10));
    y = yy; mo = mm; d = dd;
  } else if (s.includes("/")) {
    const [mm, dd, yy] = s.split("/").map((x) => parseInt(x, 10));
    mo = mm; d = dd; y = yy;
  } else {
    return s;
  }
  if (!y || !mo || !d) return s;
  return `${MONTHS[mo - 1]} ${d}, ${y}`;
}

function synthLeanSummary(b: Bundle): string {
  const others = (b.incident.outlets || []).filter((o) => o !== b.target.outlet);
  const ranked = (b.axis_profile || [])
    .filter((a) => !a.dim)
    .map((a) => ({ a, d: Math.abs(a.target_value - a.incident_mean) }))
    .sort((x, y) => y.d - x.d)
    .slice(0, 3)
    .map(({ a }) => (a.lean_label || a.label).toLowerCase());
  const tail = ranked.length ? ranked.join(", ") : "tracks the other outlets closely";
  const vs = others.length ? `Next to the other ${others.length} outlet${others.length > 1 ? "s" : ""} on this story, ` : "";
  return `${vs}this version ${tail}.`;
}

// ---- network ----
export interface BundleListItem {
  join_gva_id: number;
  city_and_state: string;
  outlet_display: string;
  headline: string;
}

const DEMO_ID = 738908;

export async function listBundles(): Promise<BundleListItem[]> {
  try {
    const r = await fetch("/api/bundles");
    if (r.ok) {
      const data = await r.json();
      if (Array.isArray(data) && data.length) return data;
    }
  } catch { /* backend not running — fall through to static demo */ }
  // static fallback: the shipped demo bundle
  try {
    const raw = await (await fetch(`/bundles/incident_bundle_${DEMO_ID}.json`)).json();
    return [{ join_gva_id: raw.incident.join_gva_id, city_and_state: raw.incident.city_and_state, outlet_display: raw.target.outlet_display || raw.target.outlet, headline: raw.target.headline }];
  } catch { return []; }
}

export async function loadBundle(id: number): Promise<Bundle> {
  // prefer the backend (serves enriched real bundles); fall back to static public/
  try {
    const r = await fetch(`/api/bundle/${id}`);
    if (r.ok) return normalize(await r.json());
  } catch { /* fall through */ }
  const raw = await (await fetch(`/bundles/incident_bundle_${id}.json`)).json();
  return normalize(raw);
}

export { DEMO_ID };
